import { useQuery } from '@tanstack/react-query';
import { DepotResource, ShiftResource, shiftsGet2 } from '~/generated';
import { extractData } from '~/utils/query-utils';
import {
  PublisherMiniResource,
  PublisherResource,
  RouteResource,
  ShiftApplicationResource,
  TurnAssignmentResource,
  TurnReportResource,
  TurnResource,
} from '~/generated';

type PublisherMiniRecord = Omit<PublisherMiniResource, 'id'> & {
  id: number;
  firstName: string;
  middleName: string;
  lastName: string;
  phone: string;
  email: string;
  languages: string[];
};

type TurnAssignment = Omit<
  TurnAssignmentResource,
  'publisher' | 'publisherMiniRecord'
> & {
  publisher: PublisherResource;
  publisherMiniRecord: PublisherMiniRecord;
};

type TurnReport = Omit<TurnReportResource, 'id'> & {
  id: number;
};

type Depot = Omit<DepotResource, 'id'> & {
  id: number;
};

type Route = Omit<RouteResource, 'id' | 'depot'> & {
  id: number;
  depot: Depot;
};

export type Turn = Omit<
  TurnResource,
  'turnAssignments' | 'id' | 'turnReport' | 'route'
> & {
  id: number;
  turnAssignments: TurnAssignment[];
  turnReport?: TurnReport;
  route: Route;
};

type ShiftApplication = Omit<ShiftApplicationResource, 'publisher' | 'turn'> & {
  publisher: PublisherResource;
  turn: Turn;
};

type Shift = Omit<ShiftResource, 'applications' | 'turns' | 'id'> & {
  id: number;
  applications: ShiftApplication[];
  turns: Turn[];
};

export const useShift = (args: { id: number; expand?: string[] }) =>
  useQuery({
    queryFn: () =>
      extractData(
        shiftsGet2({
          path: { id: args.id },
          query: { expand: args.expand },
          responseType: 'json',
        })
      ) as unknown as Shift,
    queryKey: ['shifts', args.id, args.expand],
  });
